// Inicio de sesión de clientes con Entra External ID (directorio de clientes, separado del de trabajo).
// MSAL se carga solo cuando hace falta, para que la tienda siga ligera para quien compra como invitado.
import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';

const env = import.meta.env;
const SUBDOMINIO = env.VITE_CUSTOMER_TENANT_SUBDOMAIN as string | undefined; // <sub>.ciamlogin.com
const TENANT = env.VITE_CUSTOMER_TENANT_ID as string | undefined;
const SPA = env.VITE_CUSTOMER_SPA_CLIENT_ID as string | undefined;
const API = env.VITE_CUSTOMER_API_CLIENT_ID as string | undefined;

export const cuentasActivas = Boolean(SUBDOMINIO && TENANT && SPA && API);
const scopes = [`api://${API}/customer.access`];
// Solo una marca de "hubo sesión" para saber si vale la pena cargar MSAL al abrir; nada personal
const MARCA = 'pc-sesion';

let msal: PublicClientApplication | undefined;
let cuenta: AccountInfo | null = null;

function marcar(si: boolean) {
  try {
    if (si) localStorage.setItem(MARCA, '1');
    else localStorage.removeItem(MARCA);
  } catch {
    /* modo privado */
  }
}

/** ¿Hay que cargar MSAL al abrir la tienda? (regreso de Microsoft o sesión previa) */
export function haySesionPendiente(): boolean {
  if (!cuentasActivas) return false;
  if (/[#&](code|error)=/.test(location.hash)) return true;
  try {
    return localStorage.getItem(MARCA) === '1';
  } catch {
    return false;
  }
}

async function cliente(): Promise<PublicClientApplication> {
  if (msal) return msal;
  const { PublicClientApplication } = await import('@azure/msal-browser');
  const app = new PublicClientApplication({
    auth: {
      clientId: SPA!,
      authority: `https://${SUBDOMINIO}.ciamlogin.com/${TENANT}`,
      knownAuthorities: [`${SUBDOMINIO}.ciamlogin.com`],
      redirectUri: `${location.origin}/`,
      postLogoutRedirectUri: `${location.origin}/`,
    },
    cache: { cacheLocation: 'localStorage' },
  });
  await app.initialize();
  const r = await app.handleRedirectPromise();
  cuenta = r?.account ?? app.getAllAccounts()[0] ?? null;
  if (cuenta) app.setActiveAccount(cuenta);
  marcar(!!cuenta);
  if (r) history.replaceState(null, '', location.pathname + location.search);
  msal = app;
  return app;
}

/** Termina el regreso de Microsoft o recupera la sesión guardada. */
export async function iniciarSesion(): Promise<AccountInfo | null> {
  if (!cuentasActivas) return null;
  await cliente();
  return cuenta;
}

export const usuarioActual = () => cuenta;

export async function entrar() {
  await (await cliente()).loginRedirect({ scopes });
}

export async function salir() {
  const app = await cliente();
  marcar(false);
  await app.logoutRedirect({ account: cuenta ?? undefined });
}

export async function token(): Promise<string> {
  const app = await cliente();
  if (!cuenta) throw new Error('Sin sesión');
  try {
    return (await app.acquireTokenSilent({ scopes, account: cuenta })).accessToken;
  } catch (err) {
    const { InteractionRequiredAuthError } = await import('@azure/msal-browser');
    if (err instanceof InteractionRequiredAuthError) await app.acquireTokenRedirect({ scopes, account: cuenta });
    throw err;
  }
}
